import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { ApiError } from '../api/errors';
import { landingPathForRole } from '../auth/roles';
import { useSession } from '../auth/session';
import { PasswordInput } from '../components/PasswordInput';
import { Spinner } from '../components/Spinner';
import { SignInIcon } from '../shell/icons';
import { useBrand } from '../theme/useBrand';

import './login.css';

/**
 * Which banner (if any) the form is showing. `invalid` is the deliberately
 * generic credential failure — the backend never says whether it was the email
 * or the password, and neither do we. `suspended` covers a disabled account or
 * workspace; `rate` is the too-many-attempts brake; `error` is an unexpected
 * fault (the network, a 5xx).
 */
type Banner = null | 'invalid' | 'suspended' | 'rate' | 'error';

/**
 * The sign-in screen (design §6.1): a two-column split — a branded hero on the
 * left (product lockup, a tagline, and the Askelie/ABC footer) and the sign-in
 * form on the right. The product name/logo come from the branding resolver.
 *
 * The primary action is **email + password**: it POSTs to `/v1/auth/login`,
 * which verifies the password and returns the same bearer the middleware
 * validates; the token is then adopted through `signIn`, which resolves the
 * principal from the backend (never decoded here).
 */
export function LoginScreen(): React.JSX.Element {
  const { status, role, signIn, api } = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState<Banner>(null);
  // The brand for this hostname (resolved unauthenticated; accent applied for us).
  const { brand, logoUrl } = useBrand();

  if (status === 'authenticated') {
    return <Navigate to={landingPathForRole(role)} replace />;
  }

  const brandName = brand.product_name;
  const initial = brandName.charAt(0).toUpperCase();

  const bannerFor = (error: unknown): Banner => {
    if (error instanceof ApiError) {
      if (error.status === 401) return 'invalid';
      if (error.status === 403 || error.status === 423) return 'suspended';
      if (error.status === 429) return 'rate';
    }
    return 'error';
  };

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (email.trim() === '' || password === '') return;
    setSubmitting(true);
    setBanner(null);
    try {
      const { access_token, refresh_token } = await api.auth.login({ email: email.trim(), password });
      await signIn(access_token, refresh_token);
      // Land on "/" — RootRoute sends each role to its home (a system_admin to
      // System admin, a partner_admin to their console, tenant roles to the
      // dashboard) rather than everyone to the tenant dashboard.
      void navigate('/');
    } catch (error) {
      setSubmitting(false);
      setBanner(bannerFor(error));
    }
  };

  return (
    <div className="login">
      {/* Left: the branded hero. Product lockup up top, a tagline in the middle,
          the Askelie/ABC lockup at the bottom. Hidden on narrow screens. */}
      <aside className="login-hero" aria-hidden="true">
        <div className="login-hero__brand">
          {logoUrl != null ? (
            <img className="login-hero__mark login-hero__mark--logo" src={logoUrl} alt="" />
          ) : (
            <span className="login-hero__mark">{initial}</span>
          )}
          <span className="login-hero__name">{brandName}</span>
        </div>

        <div className="login-hero__pitch">
          <h2 className="login-hero__headline">Turn every scanned document into insight.</h2>
          <p className="login-hero__sub">
            Ingest, OCR and classify at scale — then explore the analytics and ask questions in plain
            language.
          </p>
        </div>

        <p className="login-hero__footer">Powered by Askelie · an ABC Managed Solutions service</p>
      </aside>

      {/* Right: the sign-in form, vertically centred with room to breathe. */}
      <main className="login-panel">
        <div className="login-box">
          {logoUrl != null ? (
            <img className="login-mark login-mark--logo" src={logoUrl} alt="" aria-hidden="true" />
          ) : (
            <span className="login-mark" aria-hidden="true">
              {initial}
            </span>
          )}
          <h1 className="login-title">Sign in to {brandName}</h1>
          <p className="login-subtitle">Document intelligence, powered by Askelie</p>

          {banner === 'invalid' ? (
            <p className="login-banner login-banner--error" role="alert">
              That email and password don&apos;t match. Check them and try again.
            </p>
          ) : null}
          {banner === 'suspended' ? (
            <p className="login-banner login-banner--suspended" role="alert">
              This account is not active. Contact ABC Managed Solutions to restore access.
            </p>
          ) : null}
          {banner === 'rate' ? (
            <p className="login-banner login-banner--error" role="alert">
              Too many attempts. Wait a minute before trying again.
            </p>
          ) : null}
          {banner === 'error' ? (
            <p className="login-banner login-banner--error" role="alert">
              Something went wrong signing you in. Try again in a moment.
            </p>
          ) : null}

          <form
            className="login-form"
            onSubmit={(event) => {
              void submit(event);
            }}
          >
            <div className="login-field">
              <label htmlFor="login-email">Work email</label>
              <input
                id="login-email"
                className="login-input"
                type="email"
                name="email"
                placeholder="you@company.com"
                autoComplete="email"
                value={email}
                disabled={submitting}
                onChange={(event) => {
                  setEmail(event.currentTarget.value);
                }}
              />
            </div>
            <div className="login-field">
              <label htmlFor="login-password">Password</label>
              <PasswordInput
                id="login-password"
                className="login-input"
                name="password"
                placeholder="••••••••"
                autoComplete="current-password"
                value={password}
                disabled={submitting}
                onChange={(event) => {
                  setPassword(event.currentTarget.value);
                }}
              />
            </div>
            <button type="submit" className="login-submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner />
                  Signing in…
                </>
              ) : (
                <>
                  <SignInIcon />
                  Sign in
                </>
              )}
            </button>
          </form>

          <p className="login-terms">By signing in you agree to the acceptable-use policy.</p>
        </div>
      </main>
    </div>
  );
}
